import {
  getOwnPropertyDescriptor,
  isPrivateKey,
  keys,
  toReversed,
  toSorted
} from '@rehaje/shared'

import { Iterator } from '../constants'
import { dispatch } from '../dispatch'
import {
  ObserveDeleteEvent,
  ObserveSetEvent,
  ObserveSpliceEvent
} from '../event'
import { DEEP, REACTIVE } from '../flags'
import { isIteratorSymbols } from '../lambdas'
import { addParent, removeParent } from '../operations/parent'
import { batch, bind, trigger } from '../operations/register'
import { createProxy } from '../proxy'
import { undispatchDepth } from '../undispatch'

import type { ArrayFunctions, Reactive } from '../types'
import type { TArray, TProxyHandler } from '@rehaje/shared'

function arraySplice(
  target: Reactive<TArray>,
  start: number,
  deleteCount: number,
  items: any[]
): any[] {
  if (deleteCount === 0 && items.length === 0) return []
  return (
    batch(() => {
      let event
      if (undispatchDepth === 0) {
        event = dispatch(
          new ObserveSpliceEvent(target, start, deleteCount, items)
        )
        start = event.source.start
        deleteCount = event.source.deleteCount
        items = event.source.items
        if (deleteCount === 0 && items.length === 0) return []
      }
      const oldArr = target.slice()
      const deleteItems = target.splice(start, deleteCount, ...items)
      const maxLen = Math.max(oldArr.length, target.length)
      for (let i = start; i < maxLen; i++) {
        const newVal = target[i]
        const oldVal = oldArr[i]
        if (newVal !== oldVal) trigger(target, i)
        if (undispatchDepth === 0) {
          if (newVal?.__rr_flags & REACTIVE) addParent(target, i, newVal)
          removeParent(target, i, oldArr[i])
        }
      }
      trigger(target, Iterator)
      if (undispatchDepth === 0) {
        event?.__r_runEffects()
      }
      return deleteItems
    }) ?? []
  )
}

export const arrayFunctions: ArrayFunctions<Reactive<TArray>> = {
  copyWithin(this, targetIndex = 0, start = 0, end) {
    const raw = this.__rr_raw
    end ??= raw.length
    arraySplice(raw, targetIndex, end - start, raw.slice(start, end))
    return this
  },
  fill(this, value, start = 0, end) {
    const raw = this.__rr_raw
    end ??= raw.length
    arraySplice(
      raw,
      start,
      end - start,
      Array.from({ length: end - start }).fill(value)
    )
    return this
  },
  pop(this) {
    const raw = this.__rr_raw
    const len = raw.length
    return arraySplice(raw, len - 1, 1, [])[0]
  },
  push(this, ...items) {
    const raw = this.__rr_raw
    const len = raw.length
    arraySplice(raw, len, 0, items)
    return raw.length
  },
  reverse(this) {
    const raw = this.__rr_raw
    const len = raw.length
    arraySplice(raw, 0, len, toReversed(raw))
    return this
  },
  shift(this) {
    const raw = this.__rr_raw
    return arraySplice(raw, 0, 1, [])[0]
  },
  sort(this, compareFn) {
    const raw = this.__rr_raw
    const len = raw.length
    arraySplice(raw, 0, len, toSorted(raw, compareFn))
    return this
  },
  splice(this, start = 0, deleteCount = 0, ...items) {
    return arraySplice(this.__rr_raw, start, deleteCount, items)
  },
  unshift(this, ...items) {
    const raw = this.__rr_raw
    arraySplice(raw, 0, 0, items)
    return raw.length
  }
}

export const arrayHandlers: TProxyHandler<Reactive<TArray>> = {
  deleteProperty(target, key: string) {
    if (isPrivateKey(key)) delete target[key]
    else if (typeof key === 'string' && !isNaN(key) && key in target) {
      batch(() => {
        let event
        if (undispatchDepth === 0) {
          event = dispatch(new ObserveDeleteEvent(target, key))
          key = event.source.key
          if (!(key in target)) return
        }
        const oldVal = target[key]
        delete target[key]
        trigger(target, key)
        trigger(target, Iterator)
        if (undispatchDepth === 0) {
          event?.__r_runEffects()
          removeParent(target, key, oldVal)
        }
      })
    } else delete target[key]
    return true
  },
  get(target, key) {
    if (isPrivateKey(key)) return target[key]
    else if (typeof key === 'string') {
      if (key === 'length') {
        bind(target, Iterator)
        return target.length
      } else if (!isNaN(key)) {
        bind(target, key)
        const val = target[key]
        const newVal = target.__rr_flags & DEEP ? createProxy(val) : val
        if (undispatchDepth === 0 && newVal?.__rr_flags & REACTIVE)
          addParent(target, key, newVal)
        return newVal
      } else if (key in arrayFunctions) return arrayFunctions[key]
      else return target[key]
    } else if (typeof key === 'symbol') {
      if (isIteratorSymbols(key)) bind(target, Iterator)
      return target[key]
    }
  },
  getOwnPropertyDescriptor(target, key) {
    if (isPrivateKey(key)) return
    return getOwnPropertyDescriptor(target, key)
  },
  has(target, key) {
    if (isPrivateKey(key)) return key in target
    else if (typeof key === 'string') {
      if (key === 'length') bind(target, Iterator)
      else if (!isNaN(key)) bind(target, key)
    }
    return key in target
  },
  ownKeys(target) {
    bind(target, Iterator)
    return keys(target).concat('length')
  },
  set(target, key: string, value) {
    if (isPrivateKey(key)) target[key] = value
    else if (typeof key === 'string') {
      if (key === 'length') {
        if (value !== target.length) {
          batch(() => {
            let event
            if (undispatchDepth === 0) {
              event = dispatch(new ObserveSetEvent(target, key, value))
              value = event.source.value
              if (value === target.length) return
            }
            target.length = value
            trigger(target, Iterator)
            if (undispatchDepth === 0) {
              event?.__r_runEffects()
            }
          })
        }
      } else if (!isNaN(key) && value !== target[key]) {
        batch(() => {
          let event
          if (undispatchDepth === 0) {
            event = dispatch(new ObserveSetEvent(target, key, value))
            key = event.source.key
            value = event.source.value
          }
          const oldVal = target[key]
          if (value === oldVal) return
          target[key] = value
          trigger(target, key)
          trigger(target, Iterator)
          if (undispatchDepth === 0) {
            event?.__r_runEffects()
            addParent(target, key, value)
            removeParent(target, key, oldVal)
          }
        })
      }
    } else target[key] = value
    return true
  }
}
